// External globals
import type {
    CreateFormSchemaCommandData,
    FormSearchRequestData,
    ToggleSearchableCommandData,
    UpdateFormSchemaCommandData,
    UpdateFormSchemaMetaCommandData,
    UpsertFormInstanceCommandData,
} from '@/services/api-types';
import { App, updateLastAuthTokenRefresh } from '@/services/app-compat';
import { Codes } from '@/constants/codes';
import { hasValue, hasStringValue, getModuleType } from '@/utils/legacy-utils';
import { sortNatureOfCallList, sortCallDispositionList, addCompanyNameField } from '@/utils/call-card-helpers';
import { showLoadScreenIfParamTrue, hideLoadScreenIfParamTrue } from '@/utils/custom-location';
import { hasNumericValue } from '@/utils/validation';
import { getAuthObject, getCurrentUserId, setAuthObject, getCurrentAccessToken, removeAuthObject, hasReportApproval } from '@/utils/auth';
import { bowserInfo } from '@/utils/browser';
import { ModuleType, KST_AUTHENTICATE_HEADER, UnauthorizedErrors } from '@/constants/codes';
import { DATETIME_FORMAT_SERVER_UTC } from '@/constants/formats';
import { getGeolocation } from '@/utils/geolocation';
import moment from 'moment';
import type { SessionCheckResult } from '@/services/session-check-telemetry';

const $ = (window as any).$;

/// <reference path="../Scripts/typings/jquery/jquery.d.ts" />

export let isUWPApp = false; //is a UWP App, set to true by app
export let isIOSApp = false; //is an iOS App, set to true by setIOSApp() from app
export let isAndroidApp = false; //is an Android App, set to true by app
export let isWindowsAppSdkApp = false; //is Windows App SDK app, set to true by app

//Called twice - First on window.onload and then by iOS App.
export function setIOSApp(isApp: boolean) {
    isIOSApp = isApp;
    (window as any).isIOSApp = isApp;
    const appIcons = document.getElementById('loginScreenAppIcons');
    if (appIcons) appIcons.style.display = 'none';
}

setInterval(() => {
    api.resetCallFootprintIfApplicable();
}, 7000); //updates map once every 7s

export class API {
    baseURL: string = import.meta.env.VITE_API_BASE_URL || "https://orna-prod.kerkton.com:8443";

    frontEndBaseURL: string;
    api: string;
    trackUserEndpoint: string = null; //used by apps
    iosDeviceTokenEndpoint: string = null; //used by apps
    androidDeviceTokenEndpoint: string = null; //used by apps
    private apiEnabled: boolean = true;
    private backendVersion: number = null;
    private KST_LOGIN_TYPE_HEADER_VALUE: string = 'orna';

    private lastCallFootprint: string = null
    private lastCallFootprintDate = null;
    _badConnectionPopupActive: boolean = false;

    constructor() {
    }

    resetCallFootprintIfApplicable() {
        if (this.lastCallFootprintDate && moment().diff(this.lastCallFootprintDate, 'seconds') > 5) {
            this.lastCallFootprint = null;
            this.lastCallFootprintDate = null;
        }
    }

    setCurrentBackendVersion(version: number) {
        this.backendVersion = version;
        this.api = "/api/v" + version.toString() + "/"
        this.trackUserEndpoint = this.api + `mdt/status/trackuser/latlong`;
        this.iosDeviceTokenEndpoint = this.api + `devicetoken/ios`;
        this.androidDeviceTokenEndpoint = this.api + `devicetoken/android`;
    }

    getCurrentBackendVersion(): number {
        return this.backendVersion;
    }

    //Set API enabled or disabled. We want to disable the API if we do an asynchronous version check
    // and are still loading a page/APIs, but we want to refresh. This prevents errors during the refresh.
    setAPIEnabled(isEnabled: boolean) {
        this.apiEnabled = isEnabled;
    }

    isAPIEnabled(): boolean {
        return this.apiEnabled;
    }

    frontendVersionCheck(callback) {
        fetch(`/Content/orna-version.js`)
            .then(r => r.text())
            .then(result => {
                if (typeof callback === 'function') {
                    callback(result);
                }
            })
            .catch(() => {
                // Silently ignore network errors — version check is non-critical
            });
    }

    backendVersionCheck(callback) {
        fetch(`/Content/orna-backend-version.js`)
            .then(r => r.text())
            .then(result => {
                if (typeof callback === 'function') {
                    callback(result);
                }
            })
            .catch(() => {
                // Silently ignore network errors — version check is non-critical
            });
    }

    private updateLoginInfo(type: string, username: string, result) {
        //Only send this info on production.
        if (!this.baseURL.includes("live") && !this.baseURL.includes("prod")) {
            return;
        }
        const frontEndVersion: string = (hasValue(App.getCurrentFrontendVersion()) ? App.getCurrentFrontendVersion().toString() : "Error");
        const backEndVersion: string = api.getCurrentBackendVersion().toString();
        const data = { type: type, username: username, result: result, backEndVersion: backEndVersion, frontEndVersion: frontEndVersion }
        if (bowserInfo.name) {
            data["os"] = bowserInfo.osname + " " + bowserInfo.osversion;
            data["browser"] = bowserInfo.name + " " + bowserInfo.version;
        } else {
            data["userAgent"] = window.navigator.userAgent
        }
        fetch('https://orna-frontend-info-http-v4-prod.azurewebsites.net/api/login', {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data)
        });
    }

    sendGeocodeSample(raw: string, formatted: string, placeId: string, lat: number, lng: number): void {
        if (!this.baseURL.includes("live") && !this.baseURL.includes("prod")) {
            return;
        }
        const frontEndVersion: string = (hasValue(App.getCurrentFrontendVersion()) ? App.getCurrentFrontendVersion().toString() : "Error");
        const data = { raw, formatted, placeId, lat, lng, frontEndVersion };
        fetch('https://orna-frontend-info-http-v4-prod.azurewebsites.net/api/geocode-sample', {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data)
        }).catch(() => {});
    }

    /**
     * Server-side geocoding via the backend GeocodeController.
     *
     *   query       - raw user input (address string OR "lat,lng" pair)
     *   companyId   - optional; the call/context company for biasing.
     *                 Falls back to the user's primary company server-side when omitted.
     *   previousLat,
     *   previousLng - optional; previously-saved coords. Triggers server-side discrepancy
     *                 detection on edits.
     *
     * Response shape:
     *   { formatted, lat, lng, components, confidence, accuracyType, accuracyScore,
     *     discrepancyFromPrevious, alternates, learnedAddressId, lookupUnavailable }
     *
     *   lookupUnavailable is true only with confidence "none" when the lookup services
     *   could not be reached: the address is unknown, not unmatched.
     *
     * The endpoint never returns a hard failure for geocoding-quality reasons — quota
     * exhaustion, network errors, etc. all return `confidence: "none"`.
     */
    geocodeViaProxy(
        params: { query: string; companyId?: number; previousLat?: number; previousLng?: number; source?: string; acceptsUnconfirmed?: boolean },
        successCallback: (result: any) => void,
        errorCallback?: (result: any) => void
    ) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/geocode`,
            requestType: APIType.POST,
            data: JSON.stringify(params),
            noLoadScreen: true,
            successCallback,
            errorCallback
        });
    }

    /**
     * Fetch the stored HQ coordinates for a company, used as the GeocodePreview map fallback
     * center when no result is available to anchor on. Returns `{lat, lng}` when coordinates
     * have been geocoded for the company's HQ, otherwise an empty object.
     */
    getCompanyMapCenter(
        companyId: number,
        successCallback: (result: any) => void,
        errorCallback?: (result: any) => void
    ) {
        if (!hasNumericValue(companyId) || companyId <= 0) {
            if (errorCallback) errorCallback({ error: 'invalid-company-id' });
            return;
        }
        this.ajaxCall({
            url: this.api + `${getModuleType()}/geocode/company/${companyId}/map-center`,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback,
            errorCallback
        });
    }

    /**
     * List recent records flagged for supervisor address review.
     * Backs the SupervisorAddressReviewScreen.
     */
    listAddressReview(
        params: { status?: string; entityType?: string; days?: number; limit?: number },
        successCallback: (result: any) => void,
        errorCallback?: (result: any) => void
    ) {
        const queryString: string[] = [];
        if (params.status) queryString.push(`status=${encodeURIComponent(params.status)}`);
        if (params.entityType) queryString.push(`entityType=${encodeURIComponent(params.entityType)}`);
        if (params.days != null) queryString.push(`days=${params.days}`);
        if (params.limit != null) queryString.push(`limit=${params.limit}`);
        const qs = queryString.length ? `?${queryString.join('&')}` : '';
        this.ajaxCall({
            url: this.api + `super/address-review${qs}`,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: false,
            successCallback,
            errorCallback
        });
    }

    /**
     * The proactive supervisor queue: clients/sites with an address but no trusted coordinate
     * (status not HIGH/MANUAL). No days window — an unpinned record is work regardless of age.
     */
    listAddressReviewNoTrustedLocation(
        params: { entityType?: string; limit?: number },
        successCallback: (result: any) => void,
        errorCallback?: (result: any) => void
    ) {
        const queryString: string[] = [];
        if (params.entityType) queryString.push(`entityType=${encodeURIComponent(params.entityType)}`);
        if (params.limit != null) queryString.push(`limit=${params.limit}`);
        const qs = queryString.length ? `?${queryString.join('&')}` : '';
        this.ajaxCall({
            url: this.api + `super/address-review/no-trusted-location${qs}`,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: false,
            successCallback,
            errorCallback
        });
    }

    /**
     * Quick-action: mark a LOW-status record as MANUAL-verified from the supervisor dashboard.
     */
    markAddressVerified(
        entityType: string,
        entityId: number,
        successCallback: (result: any) => void,
        errorCallback?: (result: any) => void
    ) {
        this.ajaxCall({
            url: this.api + `super/address-review/mark-verified`,
            requestType: APIType.POST,
            data: JSON.stringify({ entityType, entityId }),
            noLoadScreen: true,
            successCallback,
            errorCallback
        });
    }

    /**
     * Verification history (append-only audit trail) for one address-review entity.
     * Returns {items: [{action, personId, personFirstName, personLastName, dateCreated}], total}.
     */
    getAddressVerificationAudit(
        entityType: string,
        entityId: number,
        successCallback: (result: any) => void,
        errorCallback?: (result: any) => void
    ) {
        this.ajaxCall({
            url: this.api + `super/address-review/audit?entityType=${encodeURIComponent(entityType)}&entityId=${entityId}`,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback,
            errorCallback
        });
    }

    /**
     * Undo an accidental mark-verified (MANUAL → LOW, audit columns cleared). Only valid for
     * records that were supervisor-verified; dispatcher-asserted MANUAL records return 404.
     */
    unmarkAddressVerified(
        entityType: string,
        entityId: number,
        successCallback: (result: any) => void,
        errorCallback?: (result: any) => void
    ) {
        this.ajaxCall({
            url: this.api + `super/address-review/unmark-verified`,
            requestType: APIType.POST,
            data: JSON.stringify({ entityType, entityId }),
            noLoadScreen: true,
            successCallback,
            errorCallback
        });
    }

    login(username: string, password: string, callback, errorCallback: (result: any) => void) {
        const data = { username: username, password: password };
        let geolocationOptions: GeolocationOptions = new GeolocationOptions();
        geolocationOptions.timeout = 2000;

        this.ajaxCall({
            url: this.api + "login",
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: result => {
                api.updateLoginInfo("success", username, null);
                callback(result);
            },
            errorCallback: result => {
                api.updateLoginInfo("error", username, result);
                errorCallback(result);
            },
            includeGeolocation: true,
            allowHttpCode403Forbidden: true,
            geolocationOptions: geolocationOptions,
            isLogin: true
        });
    }

    logout(callback, errorCallback: (result: any) => void): void {
        this.ajaxCall({
            url: this.api + "logout",
            requestType: APIType.POST,
            data: null,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            includeGeolocation: true
        });
    }

    isLoggedIn(callback: (result: SessionCheckResult) => void) {
        // Only a definitive answer from the server means "not logged in":
        // [0], or a 401 (unknown/expired token). Network failures, gateway
        // errors and unparseable responses are "unknown" — signing out on
        // those boots officers whenever their signal blips.
        const start = Date.now();
        this.ajaxCall({
            url: this.api + "loggedin",
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                const durationMs = Date.now() - start;
                if (Array.isArray(result) && result[0] === 1) {
                    callback({ state: 'yes', status: 200, durationMs });
                } else if (Array.isArray(result) && result[0] === 0) {
                    callback({ state: 'no', status: 200, durationMs });
                } else {
                    callback({ state: 'unknown', status: -1, durationMs });
                }
            },
            errorCallback: (result) => {
                const status: number = result?.status ?? 0;
                callback({ state: status === 401 ? 'no' : 'unknown', status, durationMs: Date.now() - start });
            },
            authTokenRefresh: false,
            allowHttpCode401Unauthorized: true,
            allowHttpCode403Forbidden: true,
            noLoadScreen: true
        });
    }

    keepAlive(): void {
        this.ajaxCall({
            url: this.api + "keepalive",
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
            },
            noLoadScreen: true
        });
    }

    reauthorizeRealtime(callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/realtime/reauthorize`,
            requestType: APIType.POST,
            data: null,
            successCallback: result => {
                callback(result);
            },
            authTokenRefresh: false,
            noLoadScreen: true
        });
    }

    getChatHistory(callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/chat/history`,
            requestType: APIType.GET,
            data: null,
            successCallback: callback,
            noLoadScreen: true
        });
    }

    sendChatMessage(data, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/chat/messages`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: callback,
            noLoadScreen: true
        });
    }

    getHome(successCallback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `home/companylogo`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getCallCards(moduleType: string, type: string, start: number, perPage: string, callback, noLoadScreen: boolean = false) {
        this.ajaxCall({
            url: this.api + `${moduleType}/callcard/list/${type}?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: null,
            noLoadScreen: noLoadScreen,
            successCallback: result => {
                callback(result);
            }
        });
    }

    getPanicCallCards(start: number, perPage: string, successCallback: (result) => void, errorCallback: (result) => void, noLoadScren: boolean = true) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/list/panic?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: noLoadScren
        });
    }

    callCardPdf(callCardId: number, timeZoneId: string, successCallback: (result) => void, errorCallback: (result) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/pdf`,
            requestType: APIType.POST,
            data: JSON.stringify({ callCardId: callCardId, timeZoneId: timeZoneId }),
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: true
        });
    }

    getAvailableCADOfficers(callCardId: number, successCallback: (result) => void, noLoadScren: boolean = true) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/availableofficers`,
            requestType: APIType.POST,
            data: hasNumericValue(callCardId) ? JSON.stringify({ callCardId: callCardId }) : "",
            successCallback: successCallback,
            noLoadScreen: noLoadScren
        });
    }

    getAvailableMDTOfficers(callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/report/availableofficers`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            },
            noLoadScreen: true
        });
    }

    getCallCardSearchOfficers(callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/officers`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            },
            noLoadScreen: true
        });
    }

    getReportSearchOfficers(callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/report/officers`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            },
            noLoadScreen: true
        });
    }

    //manual status is not in beta -AJB
    getManualStatus(callback) {
        //this.ajaxCall({
        //    url: this.api + `mdt/status/manualstatus/get`,
        //    requestType: APIType.GET,
        //    data: null,
        //    successCallback: result => {
        //        callback(result);
        //    }
        //});
        callback({});
    };

    //manual status is not in beta -AJB
    updateManualStatus(status, callback) {
        //var body = {
        //    manualStatus: status
        //};

        //this.ajaxCall({
        //    url: this.api + `mdt/status/manualstatus/update`,
        //    requestType: APIType.PUT,
        //    data: JSON.stringify(body),
        //    successCallback: result => {
        //        callback(result);
        //    }
        //});

        callback({});
    };

    listCallComments(callCardId: number, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/callcomment/list`,
            requestType: APIType.POST,
            data: JSON.stringify({ callCardId: callCardId }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    saveCallComment(callCardId: number, entry: string, saveOnClosedCall: boolean, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/callcomment/save`,
            requestType: APIType.POST,
            data: JSON.stringify({ callCardId: callCardId, entry: entry, saveOnClosedCall: saveOnClosedCall }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    updateCallComment(callCommentId: number, entry: string, updateOnClosedCall: boolean, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/callcomment/update/${callCommentId}`,
            requestType: APIType.POST,
            data: JSON.stringify({ entry: entry, updateOnClosedCall: updateOnClosedCall }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getCallCardById(callCardId, isLog: boolean, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/get/${callCardId}?log=${isLog}`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    saveCallCard(callCard: CallCard, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/save`,
            requestType: APIType.POST,
            data: this.getCleanCallCard(callCard),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    updateCallCard(callCard: CallCard, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/update/${callCard.id}`,
            requestType: APIType.PUT,
            data: this.getCleanCallCard(callCard),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    /** Writes the officer's on-scene GPS fix onto the call with OFFICER_ON_SCENE provenance. */
    confirmCallCardLocationPin(callCardId, latitude: number, longitude: number, accuracyM: number | null, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/${callCardId}/locationpin`,
            requestType: APIType.PUT,
            data: JSON.stringify({ latitude, longitude, accuracyM }),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    /** Removes the call's on-scene confirmation; the server restores the state it recorded at confirm time. */
    undoCallCardLocationPin(callCardId, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/${callCardId}/locationpin/undo`,
            requestType: APIType.PUT,
            data: JSON.stringify({}),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    /** Stops the learned location behind this call's pin being used for new calls; the call becomes LOW. */
    forgetCallCardLearnedLocation(callCardId, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/${callCardId}/learnedlocation/forget`,
            requestType: APIType.PUT,
            data: JSON.stringify({}),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    callCardReportDecision(callCardId, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/reportdecision/${callCardId}`,
            requestType: APIType.PUT,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    updateMDTCallCard(id, data, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/update/${id}`,
            requestType: APIType.PUT,
            data: this.getCleanCallCard(data),
            successCallback: result => {
                callback(result);
            },
            preventDuplicateCall: true,
        });
    }

    getMDTCallCard(id: string, isLog: boolean, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/get/${id}?log=${isLog}`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    reviseCallCard(callCard: CallCard, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/revise`,
            requestType: APIType.POST,
            data: this.getCleanCallCard(callCard),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    closeCallCard(callCard: CallCard, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/close/${callCard.id}`,
            requestType: APIType.PUT,
            data: this.getCleanCallCard(callCard),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    reopenCallCard(callCardId: number, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/reopen/${callCardId}`,
            requestType: APIType.POST,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    getCallCardAuditLogEntries(callCardId: number, start: number, perPage: string, data: any, callback) {
        const newData = hasValue(data) ? JSON.parse(data) : {};
        newData.callCard = callCardId;

        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/auditlog/entries/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: JSON.stringify(newData),
            successCallback: result => {
                callback(result);
            }
        });
    }

    forceOfficer(callCardId: number, officerId: string, authTokenRefresh: boolean, callback) {
        const body = {
            "callCardId": callCardId,
            "officerId": officerId
        };

        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/force/officer`,
            requestType: APIType.POST,
            data: JSON.stringify(body),
            successCallback: result => {
                callback(result);
            },
            authTokenRefresh: authTokenRefresh,
            preventDuplicateCall: true,
        });
    }

    saveCallCardFromPanicButton(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/panicbutton/save`,
            requestType: APIType.POST,
            data: JSON.stringify({}),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getRadioLogEntries(start: number, perPage: string, data: string, authTokenRefresh: boolean, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/log/entries/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            },
            authTokenRefresh: authTokenRefresh
        });
    }

    addRadioLogEntry(entry: string, callback) {
        const entryObj = {
            "entry": entry
        };

        this.ajaxCall({
            url: this.api + `${getModuleType()}/log/add/`,
            requestType: APIType.POST,
            data: JSON.stringify(entryObj),
            successCallback: result => {
                callback(result);
            },
        });
    }

    getAuditLogEntries(id: number, start: number, perPage: string, data: any, callback) {
        const newData = (data !== null && data !== undefined) ? JSON.parse(data) : {};
        newData["report"] = id;

        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/report/auditlog/entries/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: JSON.stringify(newData),
            successCallback: result => {
                callback(result);
            }
        });
    }

    getNPSDAuditLogEntries(id: number, npsdType: string, npsdTypeIdField: string, start: number, perPage: string, data: any, callback, errorCallback?: (error: any) => void) {
        const newData = (data !== null && data !== undefined) ? JSON.parse(data) : {};
        newData[npsdTypeIdField] = id;

        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/${npsdType}/auditlog/entries/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: JSON.stringify(newData),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback
        });
    }

    getNotesAuditLogEntries(companyId: number, start: number, perPage: string, data: any, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/ornanotes/auditlog/entries/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            }
        });
    }

    getNPSDItemById(id: string, npsdType: string, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/${npsdType}/get/${id}`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    getNPSDClientAddress(id: number, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/client/address/get/${id}`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    lockNPSDItem(id: string, isLocked: boolean, npsdType: string, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/${npsdType}/lock/${id}`,
            requestType: APIType.PUT,
            data: JSON.stringify({ locked: isLocked }),
            successCallback: result => {
                callback(result);
            }
        });
    }

    saveNPSDItem(item, npsdType: string, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/${npsdType}/save`,
            requestType: APIType.POST,
            data: this.getCleanNpsdData(item),
            successCallback: result => {
                callback(result);
            },
            preventDuplicateCall: true,
        });
    }

    updateNPSDItem(item, npsdType: string, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/${npsdType}/update/${item.id}`,
            requestType: APIType.PUT,
            data: this.getCleanNpsdData(item),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    /**
     * Store a human-placed pin for a client (provenance MANUAL) without a full-record save.
     * PUT /api/{apiVersion}/{role}/npsd/client/{id}/locationpin — same auth as client update.
     * Used by the Verify Address write-back offer; failure must never block the call, so the
     * caller fires this after resolving and only surfaces a quiet notice on error.
     */
    writeBackClientLocationPin(id: number, latitude: number, longitude: number, callback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/client/${id}/locationpin`,
            requestType: APIType.PUT,
            data: JSON.stringify({ latitude: latitude, longitude: longitude }),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    /**
     * Store a human-placed pin for a client site (provenance MANUAL) without a full-record save.
     * PUT /api/{apiVersion}/{role}/npsd/client/site/{id}/locationpin — same auth as client update.
     */
    writeBackSiteLocationPin(id: number, latitude: number, longitude: number, callback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/client/site/${id}/locationpin`,
            requestType: APIType.PUT,
            data: JSON.stringify({ latitude: latitude, longitude: longitude }),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    updateRelationships(relType: string, id: string, connectionsOld, connectionsNew, callback) {
        const body = {
            "connectionsOld": connectionsOld,
            "connectionsNew": connectionsNew
        };

        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/${relType}/connections/update/${id}`,
            requestType: APIType.PUT,
            data: JSON.stringify(body),
            successCallback: result => {
                api.getNPSDItemById(id, relType, result2 => {
                    callback(result2);
                });
            },
            preventDuplicateCall: true,
        });
    }

    npsdSearch(searchType: string, start: number, perPage: string, data: any, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/${searchType.toLowerCase()}/search/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            }
        });
    }

    callCardSearch(searchType: string, start: number, perPage: string, data: string, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/search/all/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            }
        });
    }

    callCardSearchByNpsd(npsdDataId: number, npsdType: string, npsdFieldId: string, start: number, perPage: string, data: string, callback) {
        const newData = (data !== null && data !== undefined) ? JSON.parse(data) : {};
        newData[npsdFieldId] = npsdDataId;

        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/${npsdType}/callcard/entries/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: JSON.stringify(newData),
            successCallback: result => {
                callback(result);
            }
        });
    }

    callCardSearchByLocation(callLocFormattedAddress: string, start: number, perPage: string, data: string, callback) {
        const parsed = (data !== null && data !== undefined && data !== '') ? JSON.parse(data) : { data: {} };
        parsed.data = parsed.data || {};
        parsed.data.filter = {
            logic: 'and', filters: [
                { field: 'callLocFormattedAddress', operator: 'eq', value: callLocFormattedAddress },
            ]
        };
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/search/location/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: JSON.stringify(parsed),
            successCallback: result => {
                callback(result);
            }
        });
    }

    reportSearch(searchType: string, start: number, perPage: string, data: string, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/report/search/all/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            }
        });
    }

    reportSearchByLocation(callLocFormattedAddress: string, start: number, perPage: string, data: string, callback) {
        const parsed = (data !== null && data !== undefined && data !== '') ? JSON.parse(data) : { data: {} };
        parsed.data = parsed.data || {};
        parsed.data.filter = {
            logic: 'and', filters: [
                { field: 'callLocFormattedAddress', operator: 'eq', value: callLocFormattedAddress },
            ]
        };
        this.ajaxCall({
            url: this.api + `${getModuleType()}/report/search/location/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: JSON.stringify(parsed),
            successCallback: result => {
                callback(result);
            }
        });
    }

    notesSearch(start: number, perPage: string, data: string, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/note/search/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            }
        });
    }

    saveNote(data: any, callback, errorCallback?: (result: any, failure?: AjaxFailureInfo) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/note/save`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: result => {
                callback(result);
            },
            errorCallback,
            preventDuplicateCall: true,
            timeoutMs: NOTE_WRITE_TIMEOUT_MS,
            suppressBadConnectionPopup: errorCallback !== undefined,
        });
    }

    updateNote(noteId: number, data: any, callback, errorCallback?: (result: any, failure?: AjaxFailureInfo) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/note/update/${noteId}`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: result => {
                callback(result);
            },
            errorCallback,
            preventDuplicateCall: true,
            timeoutMs: NOTE_WRITE_TIMEOUT_MS,
            suppressBadConnectionPopup: errorCallback !== undefined,
        });
    }

    deleteNote(noteId: number, callback, errorCallback?: (result: any, failure?: AjaxFailureInfo) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/note/delete/${noteId}`,
            requestType: APIType.DELETE,
            data: null,
            successCallback: result => {
                callback(result);
            },
            errorCallback,
            preventDuplicateCall: true,
            timeoutMs: NOTE_WRITE_TIMEOUT_MS,
        });
    }

    getNoteOnBehalfOfUsers(callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/note/onbehalfof/users`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            },
            noLoadScreen: true
        });
    }

    advisorySearch(start: number, perPage: string, data: string, callback, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/advisory/search/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: data,
            // The board refreshes in the background (poll / realtime push / focus);
            // the global blocking overlay would sit over open modals and swallow
            // taps. AdvisoryScreen renders its own loading/error states.
            noLoadScreen: true,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback
        });
    }

    advisoryBoard(data: any, callback, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/advisory/board`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            noLoadScreen: true,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback
        });
    }

    saveAdvisory(data: any, callback, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/advisory/save`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    updateAdvisory(advisoryId: number, data: any, callback, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/advisory/update/${advisoryId}`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    deleteAdvisory(advisoryId: number, callback, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/advisory/delete/${advisoryId}`,
            requestType: APIType.DELETE,
            data: null,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    restoreAdvisory(advisoryId: number, callback, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/advisory/restore/${advisoryId}`,
            requestType: APIType.PUT,
            data: null,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    listAdvisoryTypes(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/advisory/type/list`,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    updateAdvisoryTypes(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/advisory/type/updatelist`,
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    advisoryAuditLogEntries(start: number, perPage: string, data: any, includeViews: boolean, includeAcknowledgments: boolean, displayId: string, loggedByName: string, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        // `data` is a JSON string holding the paging operation; merge the
        // top-level includeViews / includeAcknowledgments flags (both default
        // false, hiding view and acknowledgment entries respectively). The
        // displayId (exact advisory number) and loggedByName (contains match on
        // the logger's first or last name) filters are optional: each is sent
        // only when non-blank after trim; blank/omitted means no filter.
        let body: any;
        const displayIdFilter = (displayId || '').trim();
        const loggedByNameFilter = (loggedByName || '').trim();
        const applyFilters = (obj: any) => {
            if (displayIdFilter) obj.displayId = displayIdFilter;
            if (loggedByNameFilter) obj.loggedByName = loggedByNameFilter;
            return obj;
        };
        try {
            const parsed = typeof data === 'string' ? JSON.parse(data) : (data || {});
            body = JSON.stringify(applyFilters({ ...parsed, includeViews: !!includeViews, includeAcknowledgments: !!includeAcknowledgments }));
        } catch {
            body = JSON.stringify(applyFilters({ includeViews: !!includeViews, includeAcknowledgments: !!includeAcknowledgments }));
        }
        this.ajaxCall({
            url: this.api + `${getModuleType()}/ornaadvisory/auditlog/entries/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: body,
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    acknowledgeAdvisories(callback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/advisory/acknowledge`,
            requestType: APIType.POST,
            data: null,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    getAdvisoryLatestAck(callback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/advisory/acknowledge/latest`,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
        });
    }

    // Per-advisory acknowledgment (urgent advisories only). Upserts the current
    // user's acknowledgment for this advisory; returns { acknowledgedAt, stale }.
    acknowledgeAdvisory(advisoryId: number, callback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/advisory/${advisoryId}/acknowledge`,
            requestType: APIType.POST,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    // Who acknowledged an urgent advisory (card tap-through for users with the
    // update permission). Returns [{ personId, firstName, lastName, acknowledgedAt, stale }].
    getAdvisoryAcknowledgments(advisoryId: number, callback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/advisory/${advisoryId}/acknowledgments`,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
        });
    }

    // Supervisor review-status report (gated by the audit-log role). Returns one
    // row per company person holding ROLE_ORNA_ADVISORY_READ.
    getAdvisoryReviewStatus(callback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/advisory/reviewstatus`,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
        });
    }

    // Cheap board summary for a client/site (CallCard strip): { activeCount, urgentCount }.
    getAdvisoryBoardSummary(npsdClientId: number, npsdClientSiteId: number | null, callback: (result: any) => void, errorCallback?: (result: any) => void) {
        let url = this.api + `${getModuleType()}/advisory/board/summary?npsdClientId=${npsdClientId}`;
        if (npsdClientSiteId != null) {
            url += `&npsdClientSiteId=${npsdClientSiteId}`;
        }
        this.ajaxCall({
            url: url,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
        });
    }

    getActivityReportData(data: any, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/activityreport/data`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: result => {
                callback(result);
            }
        });
    }

    getMileageActivityReportData(data: any, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/mileage/spreadsheet`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: result => {
                callback(result);
            }
        });
    }

    getActivityReportPDFBase64(data: any, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/activityreport/pdf`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: result => {
                callback(result);
            }
        });
    }

    updatePassword(data: any, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/password/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: result => {
                callback(result);
            }
        });
    }

    passwordReset(userId: string, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/user/${userId}/password/reset`,
            requestType: APIType.POST,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    recoverUsername(data: any, callback) {
        this.ajaxCall({
            url: this.api + `username/recover/send`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: result => {
                callback(result);
            }
        });
    }

    passwordResetByUsername(data: any, callback) {
        this.ajaxCall({
            url: this.api + `password/reset/send`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: result => {
                callback(result);
            }
        });
    }

    unsubscribeEmails(data: any, callback, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `unsubscribe/email/queue`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: result => {
                callback(result);
            },
            errorCallback
        });
    }

    mdtEnrouteOnSceneOrClearedNow(type: string, id: string, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        if (type !== "enroute" && type !== "onscene" && type !== "cleared") {
            api.insertNewJSError(`mdtEnrouteOnSceneOrClearedNow encountered unexpected type: ${type}`, '', null, JSErrorType.CAUGHT, null);
            return;
        }
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/${type}now/${id}`,
            requestType: APIType.PUT,
            data: JSON.stringify({}),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
        });
    }

    mdtActions(type: string, id: string, data: string, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/${type}/${id}`,
            requestType: APIType.PUT,
            data: data,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
        });
    }

    mdtAddSelfToCallCard(id: string, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callcard/addself/${id}`,
            requestType: APIType.POST,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
            preventDuplicateCall: true,
        });
    }

    getMDTList(moduleType: string, type: string, start: number, perPage: string, callback, noLoadScreen: boolean = false) {
        this.ajaxCall({
            url: this.api + `${moduleType}/callcard/list/${type}?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: null,
            noLoadScreen: noLoadScreen,
            successCallback: result => {
                callback(result);
            }
        });
    }

    getReportMDTList(type: string, start: number, perPage: string, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/report/list/${MDTInProgressStatus[type]}?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    selectModule(module, callback, errorCallback: (result: any) => void) {
        const mod = {
            module: module
        };

        this.ajaxCall({
            url: this.api + "module/select",
            requestType: APIType.POST,
            data: JSON.stringify(mod),
            successCallback: result => {
                result.ModuleType = module;
                setModuleTypeAndAvailableSubmodules(result);

                callback(result);
            },
            errorCallback: errorCallback
        });
    }

    // Feature-intro: enable Advisory Board (grants advisory perms + marks handled).
    enableAdvisory(callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "admin/advisory/intro/enable",
            requestType: APIType.POST,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback
        });
    }

    // Setup wizard: wizard + getting-started checklist state for the Home card.
    getSetupWizardStatus(callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "admin/setup-wizard/status",
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback
        });
    }

    // Setup wizard: apply answers (vertical, team size, module picks) and mark complete.
    completeSetupWizard(body, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "admin/setup-wizard/complete",
            requestType: APIType.POST,
            data: JSON.stringify(body),
            noLoadScreen: true,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback
        });
    }

    // Setup wizard: autosave in-progress answers (vertical / team size). Non-destructive —
    // does not apply terminology, revoke modules, or complete the wizard.
    saveSetupWizardDraft(body, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "admin/setup-wizard/draft",
            requestType: APIType.POST,
            data: JSON.stringify(body),
            noLoadScreen: true,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback
        });
    }

    // Feature-intro: generic dismiss for any feature key (marks handled).
    dismissFeatureIntro(featureKey: string, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/feature-intro/${featureKey}/dismiss`,
            requestType: APIType.POST,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback
        });
    }

    listGlobalNatureOfCalls(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "natureofcall/global/list",
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                sortNatureOfCallList(result)
                successCallback(result)
            },
            errorCallback: errorCallback
        });
    }

    //Used for calls for service and reports.
    listNatureOfCall(successCallback: (result: any) => void, errorCallback: (result: any) => void, includeDisabled: boolean = false) {
        const queryParam = includeDisabled ? "?includeDisabled=true" : "";
        this.ajaxCall({
            url: this.api + "natureofcall/list" + queryParam,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                sortNatureOfCallList(result.natureOfCallList)
                addCompanyNameField(result.natureOfCallList, result.companyList)
                successCallback(result)
            },
            errorCallback: errorCallback
        });
    }

    //Used in noc management.
    listCompanyNatureOfCalls(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "natureofcall/company/list",
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                sortNatureOfCallList(result)
                successCallback(result)
            },
            errorCallback: errorCallback
        });
    }

    //Used in noc management.
    getGroupAndSubgroupNocCodes(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "natureofcall/groupcodes",
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getStandardOperatingProcedures(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "natureofcall/company/sops",
            requestType: APIType.POST,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    listCompanyNatureOfCallAttachments(natureOfCallCode: number, companyId: number, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "natureofcall/company/attachments/list",
            requestType: APIType.POST,
            data: JSON.stringify({ natureOfCallCode: natureOfCallCode, companyId: companyId }),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    //Used in noc management.
    updateCompanyNatureOfCall(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "natureofcall/company/update",
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            //noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }
    updateCompanyNatureOfCallList(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "natureofcall/company/updatelist",
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }
    setNatureOfCallEnabled(natureOfCallCode: number, isEnabled: boolean, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "natureofcall/company/enabled",
            requestType: APIType.PUT,
            data: JSON.stringify({ natureOfCallCode, isEnabled }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }
    updateNatureOfCallPriority(natureOfCallCode: number, priority: number | null, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "natureofcall/company/priority",
            requestType: APIType.PUT,
            data: JSON.stringify({ natureOfCallCode, priority }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }
    addCustomNatureOfCall(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "natureofcall/company/add",
            requestType: APIType.POST,
            data: JSON.stringify(obj),
            //noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getCallAlert(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callalert/get`,
            requestType: APIType.POST,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    listCallAlerts(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callalert/list`,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    saveCallAlert(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callalert/save`,
            requestType: APIType.POST,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    updateCallAlert(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callalert/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    deleteCallAlerts(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callalert/delete`,
            requestType: APIType.POST,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    reorderCallAlerts(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callalert/reorder`,
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    listCallAlertTypes(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callalert/type/list`,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    updateCallAlertTypes(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callalert/type/updatelist`,
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getCallAlertAuditLogEntries(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/callalert/auditlog/entries`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    listCustomLocations(isLogging: boolean, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/customlocation/list` + (isLogging ? '?log=true' : ''),
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    saveCustomLocation(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/customlocation/save`,
            requestType: APIType.POST,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    updateCustomLocation(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/customlocation/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    deleteCustomLocations(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/customlocation/delete`,
            requestType: APIType.POST,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getCustomLocationAuditLogEntries(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/customlocation/auditlog/entries`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    listCompanyVehicles(isLogging: boolean, isIncludingLinkedCompanies: boolean, authTokenRefresh: boolean,
        successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        const queryParams = []
        if (isLogging) {
            queryParams.push(`log=true`)
        }
        if (isIncludingLinkedCompanies) {
            queryParams.push(`includeLinkedCompanies=true`)
        }

        this.ajaxCall({
            url: this.api + `${getModuleType()}/company/vehicle/list${queryParams.length > 0 ? '/?' : ''}${queryParams.join('&')}`,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: (result) => {
                result.companyVehicles.sort((a, b) => a.listIdx - b.listIdx);
                successCallback(result);
            },
            errorCallback: errorCallback,
            authTokenRefresh: authTokenRefresh
        });
    }

    saveCompanyVehicle(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/company/vehicle/save`,
            requestType: APIType.POST,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    deleteCompanyVehicles(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/company/vehicle/delete`,
            requestType: APIType.POST,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    reorderCompanyVehicles(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/company/vehicle/reorder`,
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    assignOfficerToVehicle(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/company/vehicle/assign`,
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    listCompanyCallSigns(isLogging: boolean, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
         this.ajaxCall({
             url: this.api + `${getModuleType()}/company/callsign/list${isLogging ? '/?log=true' : ''}`,
             requestType: APIType.GET,
             data: null,
             noLoadScreen: true,
             successCallback: (result) => {
                 result.callSigns.sort((a, b) => a.listIdx - b.listIdx);
                 successCallback(result);
             },
             errorCallback: errorCallback
         });
    }

    saveCompanyCallSign(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
         this.ajaxCall({
             url: this.api + `${getModuleType()}/company/callsign/save`,
             requestType: APIType.POST,
             data: JSON.stringify(obj),
             noLoadScreen: true,
             successCallback: successCallback,
             errorCallback: errorCallback
         });
    }

    deleteCompanyCallSigns(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
         this.ajaxCall({
             url: this.api + `${getModuleType()}/company/callsign/delete`,
             requestType: APIType.POST,
             data: JSON.stringify(obj),
             noLoadScreen: true,
             successCallback: successCallback,
             errorCallback: errorCallback
         });
    }

    reorderCompanyCallSigns(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
         this.ajaxCall({
             url: this.api + `${getModuleType()}/company/callsign/reorder`,
             requestType: APIType.PUT,
             data: JSON.stringify(obj),
             noLoadScreen: true,
             successCallback: successCallback,
             errorCallback: errorCallback
         });
    }

    getCallSignAuditLogEntries(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
         this.ajaxCall({
             url: this.api + `${getModuleType()}/company/callsign/auditlog/entries`,
             requestType: APIType.POST,
             data: JSON.stringify(data),
             successCallback: successCallback,
             errorCallback: errorCallback,
         });
    }

    // Status Update Option Management
    listStatusUpdateOptions(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/statusupdateoption/list`,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    saveStatusUpdateOption(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/statusupdateoption/save`,
            requestType: APIType.POST,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    updateStatusUpdateOption(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/statusupdateoption/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    deleteStatusUpdateOptions(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/statusupdateoption/delete`,
            requestType: APIType.POST,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    reorderStatusUpdateOptions(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/statusupdateoption/reorder`,
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    setStatusUpdateOptionEnabled(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/statusupdateoption/enabled`,
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    updateStatusUpdateSettings(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/statusupdateoption/settings`,
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getStatusUpdateHistory(params: { from: string; to: string; personId?: number | null }, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        let url = this.api + `admin/statusupdateoption/history?from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}`;
        if (params.personId != null) {
            url += `&personId=${params.personId}`;
        }
        this.ajaxCall({
            url: url,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getPermissionList(callback) {
        this.ajaxCall({
            url: this.api + "admin/permissions/list",
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    getPermissionGroups(callback) {
        this.ajaxCall({
            url: this.api + "admin/permissions/groups/list",
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    updatePermissions(permissions, callback, errorCallback?) {
        const body = {
            "permissionGroups": permissions
        };

        this.ajaxCall({
            url: this.api + "admin/permissions/update",
            requestType: APIType.PUT,
            data: JSON.stringify(body),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback
        });
    }

    getPermissionAuditLog(data: any, successCallback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "admin/permissions/auditlog/entries",
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getReportsPendingFile(showAllPendingFile: boolean, start: number, perPage: string, callback) {
        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/report/list/pending/file?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: JSON.stringify({ showAllPendingFile: showAllPendingFile, start: start, perPage: perPage }),
            successCallback: result => {
                callback(result);
            }
        });
    }

    getReportsPendingApproval(start: number, perPage: string, callback) {
        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/report/list/pending/approval?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    getReportsHistory(start: number, perPage: string, callback) {
        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/report/list/history?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    getCaseReportPdfUrl(reportId: number, successCallback: (result: any) => void, errorCallback: (result: any) => void): void {
        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/report/pdf/url`,
            requestType: APIType.POST,
            data: JSON.stringify({ reportId: reportId }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    unapproveCaseReport(reportId: number, successCallback: (result: any) => void, errorCallback: (result: any) => void): void {
        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/report/unapprove`,
            requestType: APIType.PUT,
            data: JSON.stringify({ reportId: reportId }),
            successCallback: successCallback,
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    getReport(id: string, isLog, callback) {
        this.ajaxCall({
            url: this.api + `${api.getSubModule(getModuleType())}/report/get/${id}?log=${isLog}`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    saveReport(report, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/report/save`,
            requestType: APIType.POST,
            data: this.getCleanReport(report),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    updateReport(report: mdtReport, noLoadScreen: boolean, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/report/update/${report.id}`,
            requestType: APIType.PUT,
            data: this.getCleanReport(report),
            noLoadScreen: noLoadScreen,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    listCaseReportNarrativeAttachments(reportId: number, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/report/narrative/attachments/list`,
            requestType: APIType.POST,
            data: JSON.stringify({ reportId: reportId }),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    };

    deleteReport(id: string, callback) {
        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/report/delete/${id}`,
            requestType: APIType.DELETE,
            data: null,
            successCallback: result => {
                callback(result);
            },
            preventDuplicateCall: true,
        });
    }

    lockReport(id: string, lock: boolean, callback, errorCallback: (result: any) => void) {
        const body = {
            locked: lock
        };

        this.ajaxCall({
            url: this.api + `${api.getSubModule(getModuleType())}/report/lock/${id}`,
            requestType: APIType.PUT,
            data: JSON.stringify(body),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback
        });
    }

    getOfficerStatus(authTokenRefresh: boolean, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/status/officers`,
            requestType: APIType.POST,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            authTokenRefresh: authTokenRefresh
        });
    }

    getAvailableDispatchers(authTokenRefresh: boolean, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/status/availabledispatchers`,
            requestType: APIType.POST,
            data: null,
            successCallback: result => {
                callback(result);
            },
            authTokenRefresh: authTokenRefresh
        });
    }

    trackUser(latitude: string, longitude: string, successCallback, errorCallback) {
        const body = {
            "latitude": latitude,
            "longitude": longitude
        };

        this.ajaxCall({
            url: this.trackUserEndpoint,
            requestType: APIType.PUT,
            data: JSON.stringify(body),
            successCallback: successCallback,
            errorCallback: errorCallback,
            authTokenRefresh: false,
            allowHttpCode403Forbidden: true,
            noLoadScreen: true,
        });
    }

    securityCheck(id: string, callback) {
        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/status/safetycheck/${id}`,
            requestType: APIType.PUT,
            data: JSON.stringify({id: id, role: "update"}),
            successCallback: result => {
                callback(result);
            }
        });
    }

    getStatusUpdateTargets(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/status/statusupdate/targets`,
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    statusUpdate(data: { statusComment: string, latitude: number, longitude: number, targetPersonId?: number }, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/status/statusupdate/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    clearStatus(successCallback: (result: any) => void, errorCallback: (result: any) => void, options?: { targetPersonId?: number }) {
        const query = options?.targetPersonId != null ? `?targetPersonId=${options.targetPersonId}` : '';
        this.ajaxCall({
            url: this.api + `${this.getSubModule(getModuleType())}/status/statusupdate` + query,
            requestType: APIType.DELETE,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    saveAttachment(Attachments: Array<Attachment>, callback, errorCallback?: (result: any, failure?: AjaxFailureInfo) => void) {
        const attachments = {
            attachments: Attachments
        };

        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/attachment/save`,
            requestType: APIType.POST,
            data: JSON.stringify(attachments),
            successCallback: result => {
                callback(result);
            },
            errorCallback,
        });
    }

    updateAttachment(newTitle, id, callback) {
        const attachment = {
            title: newTitle
        };

        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/attachment/update/` + id,
            requestType: APIType.PUT,
            data: JSON.stringify(attachment),
            successCallback: result => {
                callback(result);
            }
        });
    }

    getUserManagementList(start: number, perPage: string, data: string, callback) {
        this.ajaxCall({
            url: this.api + `admin/usermanagement/list/?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            }
        });
    };

    updateUser(userId, accountEnabledState: AccountEnabledStates, callback) {
        const body = {
            "enabledState": Number(accountEnabledState)
        };

        this.ajaxCall({
            url: this.api + `admin/user/${userId}/enabled`,
            requestType: APIType.PUT,
            data: JSON.stringify(body),
            successCallback: result => {
                callback(result);
            }
        });
    };

    saveUserManagement(body, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/usermanagement/user/save`,
            requestType: APIType.POST,
            data: JSON.stringify(body),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback
        });
    };

    resendRegistration(userId, successCallback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/user/${userId}/registration/resendemail`,
            requestType: APIType.POST,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    };

    getUserManagementAuditLogEntries(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/usermanagement/auditlog/entries`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }


    getUserManagementDownloadUserProfilesCsv(includeDisabledUsers: boolean, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/user/profile/all/csv${includeDisabledUsers ? "/?includeDisabledUsers=true" : ""}`,
            data: null,
            requestType: APIType.GET,
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    getUserName(callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/user/name/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    };

    getUserProfile(userId, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/user/${userId}/profile/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    };

    updateUserProfile(userId, userProfile, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/user/${userId}/profile/update`,
            requestType: APIType.PUT,
            data: this.getCleanUserProfile(userProfile),
            successCallback: result => {
                callback(result);
            }
        });
    };

    listUserProfileNoteAttachments(personId: number, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/user/${personId}/profile/notes/attachments/list`,
            requestType: APIType.POST,
            data: JSON.stringify({ personId: personId }),
            noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    };

    userProfilePdf(userId: number, successCallback: (result) => void, errorCallback: (result) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/user/${userId}/profile/pdf`,
            requestType: APIType.POST,
            data: JSON.stringify({ personId: userId }),
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: true
        });
    }

    updateOfficerModuleEntry(data: { callSign: string | null, companyVehicleId: number | null }, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
         this.ajaxCall({
             url: this.api + `mdt/officer/moduleentry`,
             requestType: APIType.PUT,
             data: JSON.stringify(data),
             successCallback: successCallback,
             errorCallback: errorCallback,
             noLoadScreen: true
         });
    }

    validateUserName(data: any, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `username/validate`,
            requestType: APIType.GET,
            data: data,
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback,
            noAuthToken: true,
            noLoadScreen: true
        });
    };

    validateRegistrationCode(code, callback) {
        this.ajaxCall({
            url: this.api + `registration/codecheck/${code}`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            },
            noAuthToken: true
        });
    };

    validatePasswordResetCode(code, callback) {
        this.ajaxCall({
            url: this.api + `password/reset/codecheck/${code}`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            },
            noAuthToken: true
        });
    };

    registerUser(data: string, callback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `registration/register`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result.usernameAvailable);
            },
            errorCallback: errorCallback,
            noAuthToken: true,
        });
    };

    resetPassword(data: string, successCallback, errorCallback) {
        this.ajaxCall({
            url: this.api + `password/reset/update`,
            requestType: APIType.PUT,
            data: data,
            successCallback: successCallback,
            errorCallback: errorCallback,
            noAuthToken: true
        });
    };

    modifyClientGroup(id, iseEnabled: boolean, callback) {
        const body = {
            isEnabled: iseEnabled
        };

        this.ajaxCall({
            url: this.api + `admin/user/${id}/enabled`,
            requestType: APIType.PUT,
            data: JSON.stringify(body),
            successCallback: result => {
                callback(result);
            }
        });
    };

    getCGMng(start: number, perPage: string, data: any, callback) {
        this.ajaxCall({
            url: this.api + `admin/npsd/client/management/list`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            }
        });
    };

    listClientGroup(successCallback, errorCallback = null, noLoadScreen = true) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/client/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                if (hasValue(errorCallback)) {
                    errorCallback(result);
                }
            },
            noLoadScreen: noLoadScreen
        });
    }

    listClientGroupSite(id, successCallback, errorCallback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/client/${id}/site/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
            noLoadScreen: true
        });
    }

    listAllClientGroupSite(successCallback, errorCallback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/client/site/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
            noLoadScreen: true
        });
    }

    updateCGMng(CGID, isEnabled, callback) {
        const body = {
            "isEnabled": isEnabled
        };

        this.ajaxCall({
            url: this.api + `admin/npsd/client/enabled/${CGID}`,
            requestType: APIType.PUT,
            data: JSON.stringify(body),
            successCallback: result => {
                callback(result);
            }
        });
    };

    getClientNotificationSummary(clientId: number, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/npsd/client/${clientId}/notificationsummary`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    };

    getCompanyProfile(callback) {
        this.ajaxCall({
            url: this.api + `admin/company/profile/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    };

    updateCompanyProfile(company, callback) {
        this.ajaxCall({
            url: this.api + `admin/company/profile/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(company),
            successCallback: result => {
                callback(result);
            }
        });
    };

    getMessagingUserInfoList(callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/messaging/users`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    };

    submitCommand(command: string, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/command/submit`,
            requestType: APIType.POST,
            data: JSON.stringify({ command: command }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getTour(id, callback) {
        this.ajaxCall({
            url: this.api + `admin/tour/get/${id}`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    };

    addTour(tour, callback) {
        this.ajaxCall({
            url: this.api + `admin/tour/save`,
            requestType: APIType.POST,
            data: JSON.stringify(tour),
            successCallback: result => {
                callback(result);
            }
        });
    };

    updateTour(tour, id, callback) {
        this.ajaxCall({
            url: this.api + `admin/tour/update/${id}`,
            requestType: APIType.PUT,
            data: JSON.stringify(tour),
            successCallback: result => {
                callback(result);
            }
        });
    };

    deleteTour(id: number, callback) {
        this.ajaxCall({
            url: this.api + `admin/tour/delete/${id}`,
            requestType: APIType.DELETE,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    enableTour(id, isEnabled, callback) {
        this.ajaxCall({
            url: this.api + `admin/tour/enabled/${id}`,
            requestType: APIType.PUT,
            data: JSON.stringify({ "isEnabled": isEnabled }),
            successCallback: result => {
                callback(result);
            }
        });
    };

    getTours(start: number, perPage: string, data, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/tour/search?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            }
        });
    };

    getTourStopsForTour(tourId, callback) {
        this.ajaxCall({
            url: this.api + `admin/tour/tourstops`,
            requestType: APIType.POST,
            data: JSON.stringify({ "tourId": tourId }),
            successCallback: result => {
                callback(result);
            }
        });
    };

    reorderTourStopsForTour(tourId: number, tourStopIds: Array<number>, callback) {
        this.ajaxCall({
            url: this.api + `admin/tour/reordertourstops`,
            requestType: APIType.POST,
            data: JSON.stringify({ tourId: tourId, tourStopIds: tourStopIds}),
            successCallback: result => {
                callback(result);
            },
            noLoadScreen: true
        });
    };

    addTourStopToTour(tourId, tourStopId, successCallback, errorCallback) {
        this.ajaxCall({
            url: this.api + `admin/tour/addtourstop`,
            requestType: APIType.PUT,
            data: JSON.stringify({
                "tourId": tourId,
                "tourStopId": tourStopId
            }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getTourStop(id, callback) {
        this.ajaxCall({
            url: this.api + `admin/tourstop/get/${id}`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    };

    addTourStop(tourStop: Kerkton.TourStop, callback) {
        this.ajaxCall({
            url: this.api + `admin/tourstop/save`,
            requestType: APIType.POST,
            data: tourStop.SerializeToJson(),
            successCallback: result => {
                callback(result);
            }
        });
    };

    updateTourStop(tourStop: Kerkton.TourStop, callback) {
        this.ajaxCall({
            url: this.api + `admin/tourstop/update/${tourStop.id}`,
            requestType: APIType.PUT,
            data: tourStop.SerializeToJson(),
            successCallback: result => {
                callback(result);
            }
        });
    };

    getCompanyTourStopReminderEmail(callback) {
        this.ajaxCall({
            url: this.api + `admin/tourstop/companyReminderEmail`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    };

    updateCompanyTourStopReminderEmail(tourStopReminderEmail: string, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `admin/tourstop/companyReminderEmail`,
            requestType: APIType.PUT,
            data: JSON.stringify({ tourStopReminderEmail: tourStopReminderEmail }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    };

    removeTourStop(tourId, tourStopId, successCallback, errorCallback) {
        this.ajaxCall({
            url: this.api + `admin/tour/removetourstop/`,
            requestType: APIType.PUT,
            data: JSON.stringify({ "tourId": tourId, "tourStopId": tourStopId }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    };

    getTourStops(start: number, perPage: string, data, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/tourstop/search?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            }
        });
    };

    getTourTasks(start: number, perPage: string, data, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/task/search`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            }
        });
    }

    getTask(taskId: number, callback) {
        this.ajaxCall({
            url: this.api + `admin/task/get/${taskId}`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    addTask(task: Kerkton.Task, callback) {
        this.ajaxCall({
            url: this.api + `admin/task/save`,
            requestType: APIType.POST,
            data: JSON.stringify(task),
            successCallback: result => {
                callback(result);
            }
        });
    }

    updateTask(task: Kerkton.Task, callback) {
        this.ajaxCall({
            url: this.api + `admin/task/update/${task.id}`,
            requestType: APIType.PUT,
            data: JSON.stringify(task),
            successCallback: result => {
                callback(result);
            }
        });
    }

    deleteTask(id, callback) {
        this.ajaxCall({
            url: this.api + `admin/task/delete/${id}`,
            requestType: APIType.DELETE,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    getTaskTourStops(taskId: number, callback) {
        this.ajaxCall({
            url: this.api + `admin/task/${taskId}/tourstops`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    assignTasksToStops(taskIds: number[], tourStopIds: number[], callback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `admin/task/assign-to-stops`,
            requestType: APIType.PUT,
            data: JSON.stringify({ taskIds, tourStopIds }),
            successCallback: result => {
                callback(result);
            },
            errorCallback: errorCallback
        });
    }

    removeTaskFromTourStop(taskId: number, tourStopId: number, callback) {
        this.ajaxCall({
            url: this.api + `admin/task/${taskId}/tourstop/${tourStopId}`,
            requestType: APIType.DELETE,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    completeTask(data, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtourstop/task/completed`,
            requestType: APIType.PUT,
            data: data,
            successCallback: result => {
                callback(result);
            }
        });
    }

    updateCompleteGuardStop(completeId, officerComments, callback) {
        const data = {
            "officerCompletionId": completeId,
            "officerComments": officerComments
        };

        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtourstop/update/completion`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: result => {
                callback(result);
            }
        });
    }

    deleteTourStop(id, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/tourstop/delete/${id}`,
            requestType: APIType.DELETE,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getTourExemptions(data, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/tour/exemption/search`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            }
        });
    }

    saveTourExemption(tourExemption: Kerkton.TourExemption, callback) {
        this.ajaxCall({
            url: this.api + `admin/tour/exemption/save`,
            requestType: APIType.POST,
            data: JSON.stringify(tourExemption),
            successCallback: result => {
                callback(result);
            }
        });
    }

    updateTourExemption(tourExemption: Kerkton.TourExemption, revisiongroup, callback) {
        this.ajaxCall({
            url: this.api + `admin/tour/exemption/update/revisiongroup/${revisiongroup}`,
            requestType: APIType.PUT,
            data: JSON.stringify(tourExemption),
            successCallback: result => {
                callback(result);
            }
        });
    }

    deleteTourExemption(revisiongroup, callback) {
        this.ajaxCall({
            url: this.api + `admin/tour/exemption/delete/revisiongroup/${revisiongroup}`,
            requestType: APIType.DELETE,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    EnableTourExemption(revisiongroup, isEnabled, callback) {
        this.ajaxCall({
            url: this.api + `admin/tour/exemption/enabled/revisiongroup/${revisiongroup}`,
            requestType: APIType.PUT,
            data: JSON.stringify({ "isEnabled": isEnabled }),
            successCallback: result => {
                callback(result);
            }
        });
    }

    getOpenGuardTours(start, perPage, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtour/list/open`,
            requestType: APIType.POST,
            data: JSON.stringify({ start: start, perPage: perPage }),
            successCallback: result => {
                callback(result);
            }
        });
    };

    getPendingGuardTours(start, perPage, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtour/list/pending`,
            requestType: APIType.POST,
            data: JSON.stringify({ start: start, perPage: perPage }),
            successCallback: result => {
                callback(result);
            }
        });
    };

    getActiveGuardTours(start, perPage, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtour/list/active`,
            requestType: APIType.POST,
            data: JSON.stringify({ start: start, perPage: perPage }),
            successCallback: result => {
                callback(result);
            }
        });
    };

    getClosedGuardTours(start, perPage, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtour/list/closed`,
            requestType: APIType.POST,
            data: JSON.stringify({ start: start, perPage: perPage }),
            successCallback: result => {
                callback(result);
            }
        });
    };

    getGuardTourSchedules(callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtour/schedule/list`,
            requestType: APIType.POST,
            data: JSON.stringify({}),
            successCallback: result => {
                callback(result);
            }
        });
    };

    claimGuardTour(id: number, isClaimed: boolean, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtour/claimed/${id}`,
            requestType: APIType.PUT,
            data: JSON.stringify({ "isClaimed": isClaimed }),
            successCallback: result => {
                callback(result);
            }
        });
    }

    getGuardTourStopsForGuardTour(guardTourId: number, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtour/guardtourstops`,
            requestType: APIType.POST,
            data: JSON.stringify({ guardTourId: guardTourId }),
            successCallback: result => {
                callback(result);
            }
        });
    }

    getGuardTourStop(guardTourStopId: number, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtourstop/get/${guardTourStopId}`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            }
        });
    }

    completeGuardTourStop(guardTourStopId: number, data, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtourstop/complete/${guardTourStopId}`,
            requestType: APIType.PUT,
            data: data,
            successCallback: result => {
                successCallback(result);
            },
            preventDuplicateCall: true,
        });
    }

    getTourStopHistory(guardTourStopId: number, data: any, callback) {
        const newData = (data !== null && data !== undefined) ? JSON.parse(data) : {};
        newData["guardTourStop"] = guardTourStopId;

        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtourstop/stophistory`,
            requestType: APIType.POST,
            data: JSON.stringify(newData),
            successCallback: result => {
                callback(result);
            }
        });
    }

    // Tour stop completion search
    searchTourStopCompletions(data: string, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtourstop/completion/search`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            }
        });
    }

    getTourStopCompletionDetail(completionId: number, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtourstop/completion/get/${completionId}`,
            requestType: APIType.GET,
            successCallback: result => {
                callback(result);
            }
        });
    }

    getTourStopCompletionOfficers(callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtourstop/completion/officers`,
            requestType: APIType.GET,
            successCallback: result => {
                callback(result);
            }
        });
    }

    getTourStopCompletionTours(callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtourstop/completion/tours`,
            requestType: APIType.GET,
            successCallback: result => {
                callback(result);
            }
        });
    }

    getTourStopCompletionPdf(completionId: number, timeZoneId: string, successCallback: (result) => void, errorCallback: (result) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtourstop/completion/pdf`,
            requestType: APIType.POST,
            data: JSON.stringify({ completionId, timeZoneId }),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }

    enableTourStop(id, isEnabled, callback) {
        this.ajaxCall({
            url: this.api + `admin/tourstop/enabled/${id}`,
            requestType: APIType.PUT,
            data: JSON.stringify({ "isEnabled": isEnabled }),
            successCallback: result => {
                callback(result);
            }
        });
    };

    getTourLogEntries(data: string, authTokenRefresh: boolean, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/tour/log`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            },
            authTokenRefresh: authTokenRefresh
        });
    }

    getGuardTourLogEntries(data: string, authTokenRefresh: boolean, callback) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/guardtour/log`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            },
            authTokenRefresh: authTokenRefresh
        });
    }

    getMapOverlay(successCallback: (result: MapOverlayGetResult) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/map/overlays/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: true
        });
    }

    updateMapOverlay(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/map/overlays/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    getActivityReportSettings(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/activityreport/settings/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    updateActivityReportSettings(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/activityreport/settings/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    getCaseReportSettings(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/report/settings/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    updateCaseReportSettings(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/report/settings/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    getPersonSettings(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `person/settings/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    updatePersonSettings(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `person/settings/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    /**
     * Save the user's officer filter preference (show online only toggle state)
     * @param isOfficerFilterOnlineOnly Whether to show only online officers
     * @param successCallback Called when save is successful
     * @param errorCallback Called if the request fails
     */
    updateOfficerFilterPreference(isOfficerFilterOnlineOnly: boolean, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `person/settings/officerfilter/update`,
            requestType: APIType.PUT,
            data: JSON.stringify({ isOfficerFilterOnlineOnly: isOfficerFilterOnlineOnly }),
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: true
        });
    }

    getUserSettings(userId: number | string, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/user/${userId}/settings/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    updateUserSettings(userId: number | string, data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/user/${userId}/settings/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    getCallCardSettings(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/callcard/settings/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    updateCallCardSettings(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/callcard/settings/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    getCompanySettings(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/company/settings/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    updateCompanySettings(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/company/settings/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    listGlobalCallDispositions(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "calldisposition/global/list",
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                sortNatureOfCallList(result)
                successCallback(result)
            },
            errorCallback: errorCallback
        });
    }

    //Used for calls for service
    listCallDispositions(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "calldisposition/list",
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                sortCallDispositionList(result.callDispositionList)
                addCompanyNameField(result.callDispositionList, result.companyList)
                successCallback(result)
            },
            errorCallback: errorCallback
        });
    }

    //Used in call management.
    listCompanyCallDispositions(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "calldisposition/company/list",
            requestType: APIType.GET,
            data: null,
            noLoadScreen: true,
            successCallback: result => {
                sortCallDispositionList(result)
                successCallback(result)
            },
            errorCallback: errorCallback
        });
    }
    updateCompanyCallDispositionList(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "calldisposition/company/updatelist",
            requestType: APIType.PUT,
            data: JSON.stringify(obj),
            //noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }
    addCustomCallDisposition(obj: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + "calldisposition/company/add",
            requestType: APIType.POST,
            data: JSON.stringify(obj),
            //noLoadScreen: true,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    getDisabledDispositionCodesList(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/dispositioncode/disabled/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }

    setDispositionCodeEnabled(data: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/dispositioncode/enabled`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
            noLoadScreen: true
        });
    }

    getDisabledNocList(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/natureofcall/disabled/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }

    getSelfInitiateNocList(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/selfinitiate/natureofcall/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }

    addSelfInitiateNoc(natureOfCall: number, isShownOnMobile: boolean, officerStatusLevel: string, secondaryNoc: number | null, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/selfinitiate/natureofcall/add`,
            requestType: APIType.POST,
            data: JSON.stringify({ natureOfCall: natureOfCall, isShownOnMobile: isShownOnMobile, officerStatusLevel: officerStatusLevel, secondaryNoc: secondaryNoc }),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
            noLoadScreen: false
        });
    }

    deleteSelfInitiateNoc(natureOfCall: number, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/selfinitiate/natureofcall/remove`,
            requestType: APIType.POST,
            data: JSON.stringify({ natureOfCall: natureOfCall }),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
            noLoadScreen: false
        });
    }

    updateSelfInitiateNoc(natureOfCall: number, isShownOnMobile: boolean, officerStatusLevel: string, secondaryNoc: number | null, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/selfinitiate/natureofcall/update`,
            requestType: APIType.POST,
            data: JSON.stringify({ natureOfCall: natureOfCall, isShownOnMobile: isShownOnMobile, officerStatusLevel: officerStatusLevel, secondaryNoc: secondaryNoc }),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
            noLoadScreen: false
        });
    }


    setEnabledNocList(data: object, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/natureofcall/enabled`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
            noLoadScreen: true
        });
    }

    resendClientPortalRegistration(userId, successCallback, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/clientperson/registration/resendemail`,
            requestType: APIType.POST,
            data: JSON.stringify({ id: userId }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    };

    getClientPortalUserList(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/clientperson/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }

    addClientPortalUser(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/clientperson/add`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
            noLoadScreen: false
        });
    }

    editClientPortalUser(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/clientperson/edit`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
            noLoadScreen: false
        });
    }

    deleteClientPortalUser(id: number, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/clientperson/delete/${id}`,
            requestType: APIType.DELETE,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
            noLoadScreen: false
        });
    }

    getClientPortalSettings(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/clientportal/settings/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    updateClientPortalSettings(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/clientportal/settings/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: false
        });
    }

    getClientPortalLogEntries(data: string, authTokenRefresh: boolean, callback) {
        this.ajaxCall({
            url: this.api + `admin/clientportal/log/entries`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                callback(result);
            },
            authTokenRefresh: authTokenRefresh
        });
    }
    //getClientPortalLogEntries(start: number, perPage: string, data: string, authTokenRefresh: boolean, callback) {
    //    this.ajaxCall({
    //        url: this.api + `admin/clientportal/log/entries/?start=${start}&perPage=${perPage}`,
    //        requestType: APIType.POST,
    //        data: data,
    //        successCallback: result => {
    //            callback(result);
    //        },
    //        authTokenRefresh: authTokenRefresh
    //    });
    //}

    getPaymentManagementInfo(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/payment/management/info`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }

    renewPaymentSubscription(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/payment/subscription/renew`,
            requestType: APIType.POST,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }

    cancelPaymentSubscription(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/payment/subscription/cancel`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }

    getSetupIntentClientSecret(attachToCurrentCompany: boolean, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/payment/card/setupintentclientsecret?attachToCurrentCompany=${(attachToCurrentCompany === true ? 'true' : 'false')}`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }

    getPricePlans(successCallback: (result) => void) {
        this.ajaxCall({
            url: this.api + `payment/prices`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                successCallback(result);
            }
        });
    }

    updateDefaultCardPaymentMethod(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/payment/card/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(data),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }

    getOrnaSupportAttachmentsList(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `admin/person/attachments/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    getCompanyDocumentsList(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `company/attachments/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }

    addCompanyDocument(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `company/attachments/add`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
            preventDuplicateCall: true,
        });
    }

    removeCompanyDocument(attachmentId: number, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `company/attachments/remove`,
            requestType: APIType.POST,
            data: JSON.stringify({ attachmentId: attachmentId }),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }


    getMapOverlayKmlList(successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/map/overlays/kml/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }

    addMapOverlayKml(data: any, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/map/overlays/kml/add`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            },
            preventDuplicateCall: true,
        });
    }

    removeMapOverlayKml(attachmentId: number, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/map/overlays/kml/remove`,
            requestType: APIType.POST,
            data: JSON.stringify({ attachmentId: attachmentId }),
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }


    getOrnaDirectoryList(data: string, successCallback: (result: any) => void, errorCallback: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `orna/directory/search`,
            requestType: APIType.POST,
            data: data,
            successCallback: result => {
                successCallback(result);
            },
            errorCallback: result => {
                errorCallback(result);
            }
        });
    }

    getCompanyLinkList(authTokenRefresh: boolean, callback) {
        this.ajaxCall({
            url: this.api + `admin/company/link/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: result => {
                callback(result);
            },
            authTokenRefresh: authTokenRefresh
        });
    }

    addCompanyLink(usernameAtOfficerCompany: string, authTokenRefresh: boolean, callback) {
        this.ajaxCall({
            url: this.api + `admin/company/link/add`,
            requestType: APIType.POST,
            data: `{ "usernameAtOfficerCompany": "${usernameAtOfficerCompany}" }`,
            successCallback: result => {
                callback(result);
            },
            authTokenRefresh: authTokenRefresh
        });
    }

    removeCompanyLink(otherCompanyId: number, otherCompanyType: string, authTokenRefresh: boolean, callback) {
        var body = {
            "otherCompanyId": otherCompanyId,
            "otherCompanyType": otherCompanyType
        };

        this.ajaxCall({
            url: this.api + `admin/company/link/remove`,
            requestType: APIType.POST,
            data: JSON.stringify(body),
            successCallback: result => {
                callback(result);
            },
            authTokenRefresh: authTokenRefresh
        });
    }

    acceptCompanyLink(otherCompanyId: number, otherCompanyType: string, authTokenRefresh: boolean, callback) {
        var body = {
            "otherCompanyId": otherCompanyId,
            "otherCompanyType": otherCompanyType
        };

        this.ajaxCall({
            url: this.api + `admin/company/link/accept`,
            requestType: APIType.POST,
            data: JSON.stringify(body),
            successCallback: result => {
                callback(result);
            },
            authTokenRefresh: authTokenRefresh
        });
    }

    analyticsReportdClientStartDates(successCallback: (result) => void, errorCallback: (result) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/analytics/report/clientstartdates/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: true
        });
    }

    analyticsReportdPdf(data: any, successCallback: (result) => void, errorCallback: (result) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/analytics/report/pdf`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback,
            noLoadScreen: true
        });
    }

    //SignUp done from signup.html and company-registration folder.

    //Send the same error a maximum of 3 times to avoid an infinite loop of errors getting sent and to prevent an infinite loop if
    // there's an error on the ajax call.
    //It is safe to use long keys. See for details: https://stackoverflow.com/a/28841863/443044
    private jsErrorMap: any = {};
    insertNewJSError(fullError: string, url: string, errorMessage: string | Event, type: string, callback: Function) {
        //don't send errors during development
        //from https://stackoverflow.com/a/3162467
        if (location.hostname === "localhost" || location.hostname === "127.0.0.1") {
            if (type === JSErrorType.UNCAUGHT) {
                console.error(fullError)
                alert("Orna JavaScript error.");
            }
            return
        }

        if (errorMessage instanceof Event) {
            errorMessage = JSON.stringify(errorMessage);
        }
        const body = {
            error: App.NtoS(fullError) + "<br />window.location.href: " + window.location.href + " -- username: " + getCurrentUsername() + " -- user id: " + getCurrentUserId(),
            url: App.NtoS(url),
            errorMessage: App.NtoS(errorMessage),
            type: App.NtoS(type),
            frontEndVersion: (hasValue(App.getCurrentFrontendVersion()) ? App.getCurrentFrontendVersion().toString() : "Error")
        };
        if (bowserInfo.name) {
            body["os"] = bowserInfo.osname + " " + bowserInfo.osversion;
            body["browser"] = bowserInfo.name + " " + bowserInfo.version;
        } else {
            body["userAgent"] = window.navigator.userAgent
        }
        const showLoadScreen = (type === JSErrorType.UNCAUGHT);

        const stringifiedBody = JSON.stringify(body);
        if (api.jsErrorMap.hasOwnProperty(stringifiedBody)) {
            api.jsErrorMap[stringifiedBody]++;
        } else {
            api.jsErrorMap[stringifiedBody] = 1;
        }
        //Send the same error a maximum of 3 times.
        if (api.jsErrorMap[stringifiedBody] > 3) {
            return;
        }

        this.ajaxCall({
            url: this.api + `jserror/log`,
            requestType: APIType.POST,
            data: stringifiedBody,
            successCallback: result => {
                if (typeof callback === 'function') {
                    callback(result);
                }
            },
            authTokenRefresh: false,
            noLoadScreen: !showLoadScreen
        });
    }

    getMapLists(authTokenRefresh: boolean, noLoadScreen: boolean, successCallback: (result) => void, errorCallback: (result) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/map/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            authTokenRefresh: authTokenRefresh,
            noLoadScreen: noLoadScreen
        });
    };

    preventDuplicateCall(url: string, requestType: APIType, data: string | null): boolean {
        const callFootprint = url + APIType[requestType] + data
        const callFootprintDate = moment();
        if (this.lastCallFootprintDate !== null && callFootprintDate.diff(this.lastCallFootprintDate, 'seconds') < 3 &&
            this.lastCallFootprint === callFootprint) {
            api.insertNewJSError('Duplicate call detected', url, null, JSErrorType.CAUGHT, null);
            return true;
        }
        this.lastCallFootprint = callFootprint;
        this.lastCallFootprintDate = callFootprintDate;
        return false;
    }

    clearDuplicateCallCheck() {
        this.lastCallFootprint = null;
        this.lastCallFootprintDate = null;
    }

    ajaxCall(params: IAjaxCallParams) {
        if (!this.isAPIEnabled()) {
            return;
        }

        const url: string = params.url;
        const requestType: APIType = params.requestType;
        const data: string | null = params.data;
        const successCallback: Function = params.successCallback;
        const showLoadScreen: boolean = (params.noLoadScreen !== true);
        const allowHttpCode401Unauthorized: boolean = (params.allowHttpCode401Unauthorized === true);
        const allowHttpCode403Forbidden: boolean = (params.allowHttpCode403Forbidden === true);
        const noAuthToken: boolean = (params.noAuthToken === true);
        const geolocationOptions: GeolocationOptions = params.geolocationOptions;
        const isLogin: boolean = (params.isLogin === true);
        const preventDuplicateCall: boolean = (params.preventDuplicateCall === true);
        const timeoutMs: number | undefined = params.timeoutMs;
        const suppressBadConnectionPopup: boolean = (params.suppressBadConnectionPopup === true);

        showLoadScreenIfParamTrue(showLoadScreen);

        let errorCallback: (result: any, failure?: AjaxFailureInfo) => void = params.errorCallback;
        if (errorCallback === undefined) {
            errorCallback = null;
        }

        if (preventDuplicateCall && this.preventDuplicateCall(url, requestType, data)) {
            hideLoadScreenIfParamTrue(showLoadScreen);
            if (errorCallback !== null) {
                errorCallback(null);
            }
            return;
        }

        let includeGeolocation: boolean = params.includeGeolocation;
        if (includeGeolocation === null || includeGeolocation === undefined) {
            includeGeolocation = false;
        }
        let authTokenRefresh: boolean = params.authTokenRefresh;

        if (authTokenRefresh !== false) {
            updateLastAuthTokenRefresh();
        }
        const accessToken = noAuthToken ? null : getCurrentAccessToken();

        const ajaxCallInner = loc => {
            const headers: any = {};

            //don't send an auth token unless one exists - sending a null auth token can break functionality
            if (accessToken !== null && accessToken !== undefined) {
                headers["X-Auth-Token"] = accessToken;
            }
            if (authTokenRefresh === false) {
                headers["X-Auth-Token-Refresh"] = false;
            }
            headers["Accept"] = "application/json; charset=utf-8";

            //if we are doing a POST or PUT, put it in the correct format
            if (APIType[requestType] === "POST" || APIType[requestType] === "PUT") {
                headers["Content-Type"] = "application/json; charset=utf-8";
            }
            //if long/lat exists, send it
            if (hasValue(loc) && hasValue(loc.coords)) {
                const latitude: number = loc.coords["latitude"];
                const longitude: number = loc.coords["longitude"];
                if (hasValue(latitude) && !isNaN(latitude) && hasValue(longitude) && !isNaN(longitude)) {
                    headers["KST-Latitude"] = latitude;
                    headers["KST-Longitude"] = longitude;
                }
            }
            if (isLogin) {
                headers["KST-Login-Type"] = api.KST_LOGIN_TYPE_HEADER_VALUE;
            }

            // Merge custom headers if provided
            if (params.customHeaders) {
                Object.assign(headers, params.customHeaders);
            }

            const fetchOptions: RequestInit = {
                method: APIType[requestType],
                headers: headers
            };

            // GET/HEAD requests cannot have a body per Fetch API spec.
            // Convert data to query parameters for GET requests (matching jQuery behavior).
            let fetchUrl = api.baseURL + url;
            if (data !== null) {
                if (requestType === APIType.GET) {
                    const params = typeof data === 'string' ? data : new URLSearchParams(data).toString();
                    fetchUrl += (url.includes('?') ? '&' : '?') + params;
                } else {
                    fetchOptions.body = data;
                }
            }

            let abortController: AbortController | null = null;
            let timeoutTimer: ReturnType<typeof setTimeout> | null = null;
            if (timeoutMs !== undefined && timeoutMs > 0 && typeof AbortController !== 'undefined') {
                abortController = new AbortController();
                fetchOptions.signal = abortController.signal;
                timeoutTimer = setTimeout(() => abortController!.abort(), timeoutMs);
            }
            const clearTimeoutTimer = () => {
                if (timeoutTimer !== null) {
                    clearTimeout(timeoutTimer);
                    timeoutTimer = null;
                }
            };

            fetch(fetchUrl, fetchOptions)
                .then(response => response.text().then(responseText => ({ response, responseText })))
                .then(({ response, responseText }) => {
                    clearTimeoutTimer();
                    let parsedJSON;
                    try { parsedJSON = JSON.parse(responseText); } catch (e) {}

                    try {
                    if (response.ok) {
                        // Success path
                        if (!this.isAPIEnabled()) {
                            return;
                        }
                        if (typeof successCallback === 'function') {
                            successCallback(parsedJSON);
                        } else {
                            if (errorCallback !== null) {
                                errorCallback(null);
                            }
                        }
                    } else {
                        // HTTP error path
                        if (!this.isAPIEnabled()) {
                            return;
                        }

                        const result: any = {
                            status: response.status,
                            statusText: response.statusText,
                            responseText: responseText,
                            responseJSON: parsedJSON,
                            getResponseHeader: (name: string) => response.headers.get(name)
                        };
                        const result3 = response.statusText;

                        if (result.responseText !== undefined && result.responseText !== null && result.responseText === '{"error":"Internal Server Error"}') {
                            if (errorCallback !== null) {
                                errorCallback(result);
                            }
                            let serverErrors = new ServerErrors(result.responseText, 1);
                            serverErrors.displayErrors();
                            return;
                        }

                        if (result.statusText === "OK") {
                            if (typeof successCallback === 'function') {
                                successCallback(result);
                                return;
                            }
                            else if (result !== undefined) {
                                if (errorCallback !== null) {
                                    errorCallback(result);
                                }
                                return;
                            }
                        }
                        if (errorCallback !== null) {
                            errorCallback(result);
                        }

                        const isUnauthorizedError: Function = (errorResult) => {
                            return (errorResult.statusText === "Unauthorized" || errorResult === "unauthorized" || errorResult.status === 401 || errorResult.responseText === '{"error":"unauthorized"}');
                        }
                        const isForbiddenError: Function = (errorResult) => {
                            return (errorResult.statusText === "Forbidden" || errorResult.statusText === "forbidden" || errorResult.status === 403 || errorResult.responseText === '{"error":"forbidden"}');
                        }

                        if (isUnauthorizedError(result)) {
                            if (!allowHttpCode401Unauthorized) {
                                App.isLoggedIn(undefined, true, '401 ' + url);
                            }
                        } else if (isForbiddenError(result)) {
                            if (!allowHttpCode403Forbidden) {
                                App.isLoggedIn(undefined, true, '403 ' + url);
                            }
                        }
                        else if (result3 === "Internal Server Error") {
                            const serverErrors = new ServerErrors(result3, 1);
                            serverErrors.displayErrors();
                        }
                        else if (result.responseJSON !== undefined && result.responseJSON.error === "access denied") {
                            ServerErrors.displayError(result.responseJSON.error);
                        }
                        else {
                            if (result.responseText === null || result.responseText === undefined || result.responseText === "") {
                                const errMessage: string = `result: ${JSON.stringify(result)}`;
                                api.insertNewJSError(errMessage, url, null, JSErrorType.CAUGHT, null);
                            } else {
                                let serverErrors;
                                try {
                                    const parsed = JSON.parse(result.responseText);
                                    serverErrors = new ServerErrors(parsed.errors._embedded !== undefined ? parsed.errors._embedded : parsed, parsed.errors.total);
                                    serverErrors.displayErrors();
                                } catch (e) {
                                    const errMessage: string = `result.responseText: ${result.responseText}`;
                                    api.insertNewJSError(errMessage, url, null, JSErrorType.CAUGHT, null);
                                }
                            }
                        }
                    }
                    } finally {
                        hideLoadScreenIfParamTrue(showLoadScreen);
                    }
                }, () => {
                    // Network error (fetch rejected) - using rejection handler so
                    // errors thrown in success/error callbacks propagate normally
                    clearTimeoutTimer();
                    if (!this.isAPIEnabled()) {
                        hideLoadScreenIfParamTrue(showLoadScreen);
                        return;
                    }
                    if (errorCallback !== null) {
                        errorCallback(null, {
                            networkError: true,
                            timedOut: abortController !== null && abortController.signal.aborted,
                        });
                    }
                    if (!suppressBadConnectionPopup && !api._badConnectionPopupActive) {
                        api._badConnectionPopupActive = true;
                        App.showPopupNotification("Bad Connection.", "error");
                        setTimeout(() => { api._badConnectionPopupActive = false; }, 5000);
                    }
                    hideLoadScreenIfParamTrue(showLoadScreen);
                });
        };

        const gelocationSuccess: PositionCallback = ajaxCallInner;
        const geolocationFail: PositionErrorCallback = (err) => {
            ajaxCallInner(null);
        }

        if (includeGeolocation) {
            getGeolocation(gelocationSuccess, geolocationFail, geolocationOptions);
        } else {
            ajaxCallInner(null);
        }
    }

    // ==================== CITATION API METHODS ====================

    /**
     * Search citations with filters
     * POST /api/{apiVersion}/{role}/citation/search
     */
    searchCitations(
        filters: {
            sortField?: string;
            sortDir?: string;
            citationNumber?: string;
            offenderName?: string;
            location?: string;
            dateFrom?: string;
            dateTo?: string;
            status?: string;
            officerId?: number;
            violationType?: number;
            hasLinkedCall?: boolean;
        },
        start: number,
        perPage: number,
        successCallback: (result: any) => void,
        errorCallback?: (result: any) => void
    ) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/search?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: JSON.stringify(filters),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * Get single citation by ID
     * GET /api/{apiVersion}/{role}/citation/get/{id}
     */
    getCitationById(id: number, successCallback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/get/${id}`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * Get citations for a specific person
     * POST /api/{apiVersion}/{role}/citation/person/{personId}/entries
     */
    getCitationsForPerson(
        personId: number,
        filters: { sortField?: string; sortDir?: string; status?: string },
        start: number,
        perPage: number,
        successCallback: (result: any) => void,
        errorCallback?: (result: any) => void
    ) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/person/${personId}/entries?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: JSON.stringify(filters),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * Export citations for a specific person to CSV
     * POST /api/{apiVersion}/{role}/citation/person/{personId}/export/csv
     */
    exportCitationsForPerson(
        personId: number,
        filters: {
            sortField?: string;
            sortDir?: string;
            dateFrom?: string;
            dateTo?: string;
            status?: string;
        },
        successCallback: (result: any) => void,
        errorCallback?: (result: any) => void
    ) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/person/${personId}/export/csv`,
            requestType: APIType.POST,
            data: JSON.stringify(filters),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * Get citations for a specific call card
     * POST /api/{apiVersion}/{role}/citation/callcard/{callCardId}/entries
     */
    getCitationsForCallCard(
        callCardId: number,
        filters: { sortField?: string; sortDir?: string; status?: string },
        start: number,
        perPage: number,
        successCallback: (result: any) => void,
        errorCallback?: (result: any) => void
    ) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/callcard/${callCardId}/entries?start=${start}&perPage=${perPage}`,
            requestType: APIType.POST,
            data: JSON.stringify(filters),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * Create a new citation (draft)
     * POST /api/{apiVersion}/{role}/citation/save
     */
    saveCitation(citation: Citation, successCallback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/save`,
            requestType: APIType.POST,
            data: this.getCleanCitation(citation),
            successCallback: successCallback,
            errorCallback: errorCallback,
            preventDuplicateCall: true
        });
    }

    /**
     * Update an existing citation
     * PUT /api/{apiVersion}/{role}/citation/update/{id}
     */
    updateCitation(citation: Citation, lastKnownModifiedDate?: Date, successCallback?: (result: any) => void, errorCallback?: (result: any) => void) {
        const data: any = this.cleanForSerialization(citation);
        // Transform attachmentList to attachmentInfoList for API compatibility
        // API expects 'attachmentInfoList' when saving/updating, but returns 'attachmentList' when reading
        if (data.attachmentList !== undefined) {
            data.attachmentInfoList = data.attachmentList;
            delete data.attachmentList;
        }
        if (lastKnownModifiedDate) {
            data['lastKnownModifiedDate'] = moment(lastKnownModifiedDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z";
        }
        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/update/${citation.id}`,
            requestType: APIType.PUT,
            data: JSON.stringify(data, this.citationReplacer),
            successCallback: successCallback,
            errorCallback: errorCallback,
            preventDuplicateCall: true
        });
    }

    /**
     * Delete a draft citation
     * DELETE /api/{apiVersion}/{role}/citation/delete/{id}
     */
    deleteCitation(id: number, successCallback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/delete/${id}`,
            requestType: APIType.DELETE,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * Issue a citation (draft -> issued)
     * POST /api/{apiVersion}/{role}/citation/issue/{id}
     */
    issueCitation(id: number, lastKnownModifiedDate?: Date, successCallback?: (result: any) => void, errorCallback?: (result: any) => void) {
        let data: any = null;

        if (lastKnownModifiedDate) {
            const formattedDate = moment(lastKnownModifiedDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z";
            data = JSON.stringify({ lastKnownModifiedDate: formattedDate });
        }

        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/issue/${id}`,
            requestType: APIType.POST,
            data: data,
            successCallback: successCallback,
            errorCallback: errorCallback,
            preventDuplicateCall: true
        });
    }

    /**
     * Void an issued citation
     * POST /api/{apiVersion}/{role}/citation/void/{id}
     */
    voidCitation(id: number, reason: string, lastKnownModifiedDate?: Date, successCallback?: (result: any) => void, errorCallback?: (result: any) => void) {
        const data: any = { reason: reason };

        if (lastKnownModifiedDate) {
            const formattedDate = moment(lastKnownModifiedDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z";
            data.lastKnownModifiedDate = formattedDate;
        }

        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/void/${id}`,
            requestType: APIType.POST,
            data: JSON.stringify(data),
            successCallback: successCallback,
            errorCallback: errorCallback,
            preventDuplicateCall: true
        });
    }

    /**
     * Generate citation PDF
     * POST /api/{apiVersion}/{role}/citation/pdf
     */
    getCitationPdf(citationId: number, timeZoneId: string, version: 'public' | 'internal', successCallback: (result: any) => void, errorCallback?: (result: any) => void, includeAttachmentImages: boolean = false) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/pdf`,
            requestType: APIType.POST,
            data: JSON.stringify({ citationId: citationId, timeZoneId: timeZoneId, version: version, includeAttachmentImages: includeAttachmentImages }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * Export citations to CSV
     * POST /api/{apiVersion}/{role}/citation/export/csv
     */
    exportCitations(
        filters: {
            sortField?: string;
            sortDir?: string;
            dateFrom: string;
            dateTo: string;
            status?: string;
            officerId?: number;
            citationNumber?: string;
            offenderName?: string;
            location?: string;
        },
        successCallback: (result: any) => void,
        errorCallback?: (result: any) => void
    ) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/export/csv`,
            requestType: APIType.POST,
            data: JSON.stringify(filters),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * Get citation audit log
     * POST /api/{apiVersion}/{role}/citation/auditLog
     */
    getCitationAuditLog(
        citationId: number,
        data: { page?: number; pageSize?: number; sortField?: string; sortDir?: string; filter?: any },
        includeViews: boolean,
        successCallback: (result: any) => void,
        errorCallback?: (result: any) => void
    ) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/auditLog`,
            requestType: APIType.POST,
            data: JSON.stringify({ citationId: citationId, includeViews: includeViews, data: data }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * Get citation settings
     * GET /api/{apiVersion}/{role}/citation/settings/get
     */
    getCitationSettings(successCallback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/settings/get`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * Update citation settings
     * PUT /api/{apiVersion}/{role}/citation/settings/update
     */
    updateCitationSettings(settings: { isCourtDateEnabled?: boolean; isPublicNotesEnabled?: boolean; offenderMode?: number }, successCallback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/citation/settings/update`,
            requestType: APIType.PUT,
            data: JSON.stringify(settings),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    // ==================== VIOLATION TYPE API METHODS ====================

    /**
     * List violation types for citation forms
     * GET /api/{apiVersion}/violationtype/list
     */
    getViolationTypes(successCallback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `violationtype/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * List company violation types (for management)
     * GET /api/{apiVersion}/violationtype/company/list
     */
    getCompanyViolationTypes(successCallback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `violationtype/company/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * List global violation types
     * GET /api/{apiVersion}/violationtype/global/list
     */
    getGlobalViolationTypes(successCallback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `violationtype/global/list`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * Add company-specific violation type
     * POST /api/{apiVersion}/violationtype/company/add
     */
    addCompanyViolationType(value: string, successCallback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `violationtype/company/add`,
            requestType: APIType.POST,
            data: JSON.stringify({ value: value }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    /**
     * Update company violation type list
     * PUT /api/{apiVersion}/violationtype/company/updatelist
     */
    updateViolationTypeList(violationTypeList: Array<{ code: number; listIdx: number }>, successCallback: (result: any) => void, errorCallback?: (result: any) => void) {
        this.ajaxCall({
            url: this.api + `violationtype/company/updatelist`,
            requestType: APIType.PUT,
            data: JSON.stringify({ violationTypeList: violationTypeList }),
            successCallback: successCallback,
            errorCallback: errorCallback
        });
    }

    //Clean top-level elements from __obj__ injection due to vue.
    cleanForSerialization(obj) {
        const cleaned = {};

        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                const value = obj[key];

                // Skip functions and problematic objects
                if (typeof value === 'function') {
                    continue;
                }

                // If this property has __ob__, strip it with circular-safe stringify
                if (value && typeof value === 'object' && value.__ob__) {
                    try {
                        cleaned[key] = JSON.parse(JSON.stringify(value, getCircularReplacer()));
                    } catch (e) {
                        console.warn(`Could not serialize property '${key}':`, e);
                        cleaned[key] = null; // or undefined, or skip it
                    }
                } else {
                    cleaned[key] = value;
                }
            }
        }

        return cleaned;
    }

    getCleanCallCard(callCard) {
        const cleanedCallCard = this.cleanForSerialization(callCard);
        return JSON.stringify(cleanedCallCard, this.callCardReplacer);
    }
    callCardReplacer(name, val) {
        // replaces parts of calls that have to correspond to a certain format.
        // for example a datetime has to be in this format (YYYY-MM-DDTHH:mm:ss.SSSZ) or the server will send back an error
        if (typeof (val) === "string" && CallCard.isDateField(name)) {
            return (moment(val).utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z");
        } else {
            return val;
        }
    }


    getCleanReport(report) {
        const cleanedReport = this.cleanForSerialization(report);
        return JSON.stringify(cleanedReport, this.reportReplacer);
    }
    reportReplacer(name, val) {
        // replaces parts of calls that have to correspond to a certain format.
        // for example a datetime has to be in this format (YYYY-MM-DDTHH:mm:ss.SSSZ) or the server will send back an error
        if (typeof (val) === "string" && mdtReport.isDateField(name)) {
            return (moment(val).utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z");
        } else {
            return val;
        }
    }

    getCleanNpsdData(npsdItem) {
        const cleanedNpsdData = this.cleanForSerialization(npsdItem);
        return JSON.stringify(cleanedNpsdData, this.npsdDataReplacer);
    }
    npsdDataReplacer(name, val) {
        // replaces parts of calls that have to correspond to a certain format.
        // Date-only fields (DOB, license dates, etc.) should be formatted in UTC to prevent timezone shift
        if (typeof (val) === "string" && (Person.isDateField(name) || Vehicle.isDateField(name))) {
            return (moment.utc(val).format("YYYY-MM-DD"));
        } else {
            return val;
        }
    }

    getCleanUserProfile(userProfileData) {
        const cleanedUserProfile = this.cleanForSerialization(userProfileData);
        return JSON.stringify(cleanedUserProfile, this.profileDataReplacer);
    }
    profileDataReplacer(name, val) {
        // replaces parts of calls that have to correspond to a certain format.
        // Date-only fields should be formatted in UTC to prevent timezone shift
        if (typeof (val) === "string" && (name === "dob" || name === "hireDate")) {
            return (moment.utc(val).format("YYYY-MM-DD"));
        } else {
            return val;
        }
    }

    getCleanCitation(citation) {
        const cleanedCitation: any = this.cleanForSerialization(citation);
        // Transform attachmentList to attachmentInfoList for API compatibility
        // API expects 'attachmentInfoList' when saving/updating, but returns 'attachmentList' when reading
        if (cleanedCitation.attachmentList !== undefined) {
            cleanedCitation.attachmentInfoList = cleanedCitation.attachmentList;
            delete cleanedCitation.attachmentList;
        }
        return JSON.stringify(cleanedCitation, this.citationReplacer);
    }
    citationReplacer(name, val) {
        // replaces parts of citation that have to correspond to a certain format.
        // datetime fields need to be in this format (YYYY-MM-DDTHH:mm:ss.SSSZ) for the server
        if (typeof (val) === "string" && Citation.isDateField(name)) {
            return (moment(val).utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z");
        } else {
            return val;
        }
    }

    getSubModule(item) {
        if (hasReportApproval())
            return "super";
        else
            return item;
    }

    // ── Custom Forms — schemas ─────────────────────────────────────────
    // Plan §2.5. Schema CRUD is admin-only; URL is hard-coded `admin/...` (no submodule prefix).

    getFormSchemas(attachTo: string | null, successCallback, errorCallback?) {
        const query = attachTo ? `?attachTo=${encodeURIComponent(attachTo)}` : '';
        this.ajaxCall({
            url: this.api + `admin/form/schema/list${query}`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    // ── Custom Forms — display path (FormSchemaReadController) ────────
    // Module-scoped read endpoints so rendering form tabs does not require
    // ROLE_FORM_MANAGEMENT. Active schemas only, current version only —
    // authoring history stays behind the admin CRUD methods above.

    getFormSchemasForDisplay(attachTo: string | null, successCallback, errorCallback?) {
        const query = attachTo ? `?attachTo=${encodeURIComponent(attachTo)}` : '';
        this.ajaxCall({
            url: this.api + `${getModuleType()}/form/schema/display/list${query}`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    getFormSchemaForDisplay(id: number, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/form/schema/display/get/${id}`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    /**
     * Lean projection of which schemas have searchable fields, used by Records
     * Search to render its per-schema filter sections. Returns an array of
     * `FormSchemaSearchableFieldsData` — one entry per active schema with at
     * least one searchable field.
     *
     * Module-prefixed (admin/cad/mdt) like the form instance endpoints because
     * Records Search is invoked from any of those staff modules, not just the
     * admin schema-authoring flow.
     */
    getSearchableFormFields(attachTo: string | null, successCallback, errorCallback?) {
        const query = attachTo ? `?attachTo=${encodeURIComponent(attachTo)}` : '';
        this.ajaxCall({
            url: this.api + `${getModuleType()}/form/schema/searchable-fields${query}`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    /**
     * Read-only projection used by the live tour stop detail page to render
     * extra tabs (alongside Tasks / Comments / Attachments) populated with
     * fields from form instances on the stop's client whose schemas have
     * tab-flagged fields. Returns `FormTabData[]` — one entry per visible
     * instance. Empty when the stop has no qualifying instances.
     *
     * Module-prefixed (admin/cad/mdt) — both dispatchers and field officers
     * see the tabs, so the URL adapts to whichever module is loaded.
     */
    getTourStopFormTabs(tourStopId: number, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/tourstop/${tourStopId}/form-tabs`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    getFormSchema(id: number, versionNumber: number | null, successCallback, errorCallback?) {
        const query = versionNumber != null ? `?versionNumber=${versionNumber}` : '';
        this.ajaxCall({
            url: this.api + `admin/form/schema/get/${id}${query}`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    createFormSchema(cmd: CreateFormSchemaCommandData, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `admin/form/schema/create`,
            requestType: APIType.POST,
            data: JSON.stringify(cmd),
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    updateFormSchema(id: number, cmd: UpdateFormSchemaCommandData, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `admin/form/schema/update/${id}`,
            requestType: APIType.PUT,
            data: JSON.stringify(cmd),
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    updateFormSchemaMeta(id: number, cmd: UpdateFormSchemaMetaCommandData, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `admin/form/schema/updateMeta/${id}`,
            requestType: APIType.PUT,
            data: JSON.stringify(cmd),
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    previewUpdateFormSchema(id: number, cmd: UpdateFormSchemaCommandData, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `admin/form/schema/preview-update/${id}`,
            requestType: APIType.POST,
            data: JSON.stringify(cmd),
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    /**
     * Preview the impact of toggling a single field's searchable flag. Used by the
     * schema editor before committing the change so the frontend can render a
     * severity-tiered confirmation. Read-only; no schema state changes.
     */
    previewSearchableToggle(schemaId: number, fieldKey: string,
                            cmd: ToggleSearchableCommandData,
                            successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `admin/form/schema/${schemaId}/field/${encodeURIComponent(fieldKey)}/searchable/preview`,
            requestType: APIType.POST,
            data: JSON.stringify(cmd),
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    /**
     * Apply a searchable-flag toggle on a single existing field. The regular /update
     * endpoint rejects this case (.searchable.flagChangeNotAllowed); this is the only
     * supported path. Bumps a new schema version and triggers the post-commit reindex.
     */
    toggleSearchable(schemaId: number, fieldKey: string,
                     cmd: ToggleSearchableCommandData,
                     successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `admin/form/schema/${schemaId}/field/${encodeURIComponent(fieldKey)}/searchable`,
            requestType: APIType.PUT,
            data: JSON.stringify(cmd),
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    deleteFormSchema(id: number, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `admin/form/schema/${id}`,
            requestType: APIType.DELETE,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    previewDeleteFormSchema(id: number, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `admin/form/schema/preview-delete/${id}`,
            requestType: APIType.POST,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    reindexFormSchema(id: number, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `admin/form/schema/reindex/${id}`,
            requestType: APIType.POST,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    // ── Custom Forms — instances ──────────────────────────────────────
    // Plan §2.5. Instance endpoints use the current module prefix (admin/cad/mdt) so both
    // ORNA Settings authoring AND officers' tour stop rendering can hit them.

    getFormInstances(recordType: string, recordId: number, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/form/instance/list?recordType=${encodeURIComponent(recordType)}&recordId=${recordId}`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    getFormInstance(id: number, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/form/instance/get/${id}`,
            requestType: APIType.GET,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    upsertFormInstance(cmd: UpsertFormInstanceCommandData, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/form/instance/upsert`,
            requestType: APIType.POST,
            data: JSON.stringify(cmd),
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }

    deleteFormInstance(id: number, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/form/instance/${id}`,
            requestType: APIType.DELETE,
            data: null,
            successCallback: successCallback,
            errorCallback: errorCallback,
            preventDuplicateCall: true,
        });
    }

    searchFormInstances(request: FormSearchRequestData, successCallback, errorCallback?) {
        this.ajaxCall({
            url: this.api + `${getModuleType()}/form/instance/search`,
            requestType: APIType.POST,
            data: JSON.stringify(request),
            successCallback: successCallback,
            errorCallback: errorCallback,
        });
    }
}

// Note writes are small; past this the connection is gone and the guard should be able to retry.
const NOTE_WRITE_TIMEOUT_MS = 30000;

export interface IAjaxCallParams {
    url: string;
    requestType: APIType;
    data: string | null;
    successCallback: Function;
    errorCallback?: (result: any, failure?: AjaxFailureInfo) => void;
    includeGeolocation?: boolean;
    authTokenRefresh?: boolean;
    noLoadScreen?: boolean;
    allowHttpCode401Unauthorized?: boolean;
    allowHttpCode403Forbidden?: boolean;
    noAuthToken?: boolean;
    geolocationOptions?: GeolocationOptions;
    isLogin?: boolean;
    preventDuplicateCall?: boolean;
    customHeaders?: Record<string, string>;
    // Abort the request after this many milliseconds. A phone on a dead connection can
    // otherwise leave fetch pending for minutes with the loading overlay up.
    timeoutMs?: number;
    // Skip the global "Bad Connection." popup; for callers that show their own message.
    suppressBadConnectionPopup?: boolean;
}

// Second argument to errorCallback when the request never got an HTTP response.
export interface AjaxFailureInfo {
    networkError: boolean;
    timedOut: boolean;
}

export class GeolocationOptions {
    enableHighAccuracy: boolean = true;
    timeout: number = 10000;
    maximumAge: number = 0;
}

export enum APIType {
    GET,
    POST,
    PUT,
    DELETE
}

export let api = new API();

// Window registrations
(window as any).api = api;
(window as any).API = API;
(window as any).APIType = APIType;
(window as any).GeolocationOptions = GeolocationOptions;
(window as any).isUWPApp = isUWPApp;
(window as any).isIOSApp = isIOSApp;
(window as any).isAndroidApp = isAndroidApp;
(window as any).isWindowsAppSdkApp = isWindowsAppSdkApp;
(window as any).setIOSApp = setIOSApp;
